import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const COLLECTIONS = [
  { key: "all", label: "All" },
  { key: "pulse", label: "PULSE — Performance" },
  { key: "core", label: "CORE — Casual" },
  { key: "accessories", label: "Accessories" },
];
const GENDERS = [
  { key: "men", label: "Men" },
  { key: "women", label: "Women" },
];
const ACC_TYPES = [
  { key: "hats", label: "Hats" },
  { key: "bottles", label: "Water Bottles" },
  { key: "socks", label: "Socks" },
];
const SIZE_ORDER = ["2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "4", "6", "8", "10", "12", "ONE-SIZE", "500ml", "OS"];

const COLLECTION_HEADER = {
  all: { eyebrow: "The collection", title: "Shop" },
  pulse: { eyebrow: "Performance", title: "PULSE" },
  core: { eyebrow: "Casual", title: "CORE" },
  accessories: { eyebrow: "Gear", title: "Accessories" },
};

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const collection = params.get("collection") || "all";
  const gender = params.get("gender") || null;
  const accType = params.get("type") || null;
  const [products, setProducts] = useState([]);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (collection && collection !== "all") q.set("collection", collection);
    if (gender) q.set("gender", gender);
    if (accType) q.set("accessory_type", accType);
    api.get(`/products?${q.toString()}`).then((r) => setProducts(r.data)).catch(() => {});
  }, [collection, gender, accType]);

  const filtered = useMemo(() =>
    products.filter((p) =>
      (!size || p.sizes.includes(size)) &&
      (!color || p.colors.some((c) => c.name === color))
    ), [products, size, color]);

  const sizeOptions = useMemo(() => {
    const s = new Set(products.flatMap((p) => p.sizes));
    return SIZE_ORDER.filter((x) => s.has(x)).concat([...s].filter((x) => !SIZE_ORDER.includes(x)));
  }, [products]);
  const colorOptions = useMemo(
    () => [...new Set(products.flatMap((p) => p.colors.map((c) => c.name)))], [products]);

  const setCollection = (key) => {
    const next = new URLSearchParams();
    if (key !== "all") next.set("collection", key);
    if (gender) next.set("gender", gender);
    setParams(next);
    setSize(null); setColor(null);
  };
  const setGender = (key) => {
    const next = new URLSearchParams(params);
    if (gender === key) next.delete("gender"); else next.set("gender", key);
    setParams(next);
  };
  const setAccType = (key) => {
    const next = new URLSearchParams(params);
    if (accType === key) next.delete("type"); else next.set("type", key);
    setParams(next);
  };

  const head = COLLECTION_HEADER[collection] || COLLECTION_HEADER.all;

  return (
    <div className="bg-ink min-h-screen pt-32 pb-28" data-testid="shop-page">
      <div className="container-sync">
        <motion.div key={collection} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="eyebrow mb-4">{head.eyebrow}</p>
          <h1 className="font-anton uppercase text-6xl sm:text-8xl leading-none">{head.title}</h1>
          {collection === "pulse" && (
            <p className="mt-6 text-graphite text-lg max-w-2xl">Performance apparel engineered for the gym, the run, the sport — nothing fighting you, nothing holding you back.</p>
          )}
          {collection === "core" && (
            <p className="mt-6 text-graphite text-lg max-w-2xl">Everyday essentials with premium hand-feel. From the walk to the corner store to the flight to somewhere new.</p>
          )}
          {collection === "accessories" && (
            <p className="mt-6 text-graphite text-lg max-w-2xl">The gear that moves with you — dialed, durable, and quietly designed to match.</p>
          )}
        </motion.div>

        {/* Collection selector */}
        <div className="flex flex-wrap gap-3 mt-14">
          {COLLECTIONS.map((c) => (
            <button key={c.key} onClick={() => setCollection(c.key)} data-testid={`filter-collection-${c.key}`}
              className={`font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 border transition-colors ${
                collection === c.key ? "bg-chalk text-ink border-chalk" : "border-border text-chalk/70 hover:border-chalk"
              }`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Gender selector */}
        <div className="flex flex-wrap gap-3 mt-4">
          {GENDERS.map((gopt) => (
            <button key={gopt.key} onClick={() => setGender(gopt.key)} data-testid={`filter-gender-${gopt.key}`}
              className={`font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-2 border transition-colors ${
                gender === gopt.key ? "bg-signal text-ink border-signal" : "border-border text-chalk/70 hover:border-chalk"
              }`}>
              {gopt.label}
            </button>
          ))}
        </div>

        {/* Sub-filters */}
        {collection === "accessories" && (
          <div className="flex flex-wrap gap-3 mt-4">
            {ACC_TYPES.map((sub) => (
              <button key={sub.key} onClick={() => setAccType(sub.key)} data-testid={`filter-sub-${sub.key}`}
                className={`font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${
                  accType === sub.key ? "bg-signal text-ink border-signal" : "border-border text-chalk/70 hover:border-chalk"
                }`}>
                {sub.label}
              </button>
            ))}
          </div>
        )}

        {/* Attribute filters */}
        <div className="flex flex-wrap items-center gap-6 pb-10 mt-6 border-b border-border">
          <div className="flex items-center gap-2 max-w-full overflow-x-auto pb-1" data-testid="filter-size-row">
            <span className="eyebrow mr-2 shrink-0">Size</span>
            {sizeOptions.map((s) => (
              <button key={s} onClick={() => setSize(size === s ? null : s)} data-testid={`filter-size-${s}`}
                className={`font-mono text-[11px] min-w-9 h-9 px-2 border transition-colors shrink-0 ${
                  size === s ? "bg-signal text-ink border-signal" : "border-border text-chalk/70 hover:border-chalk"
                }`}>{s}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 max-w-full overflow-x-auto pb-1" data-testid="filter-color-row">
            <span className="eyebrow mr-2 shrink-0">Color</span>
            {colorOptions.map((c) => (
              <button key={c} onClick={() => setColor(color === c ? null : c)} data-testid={`filter-color-${c}`}
                className={`font-mono text-[11px] uppercase px-3 h-9 border transition-colors shrink-0 ${
                  color === c ? "bg-signal text-ink border-signal" : "border-border text-chalk/70 hover:border-chalk"
                }`}>{c}</button>
            ))}
          </div>
        </div>

        <p className="font-mono text-xs text-graphite uppercase tracking-widest mt-8 mb-10" data-testid="result-count">
          {filtered.length} product{filtered.length !== 1 ? "s" : ""}
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        {filtered.length === 0 && (
          <p className="text-graphite font-mono uppercase tracking-widest mt-10">No products match these filters.</p>
        )}
      </div>
    </div>
  );
}
